import React, { useEffect, useCallback, useState } from 'react';
import { useHandleError } from '../../services';
import { gqlGetCollections } from './graphql';
import { useLazyQuery } from '@apollo/client';
import { Card, Button } from 'react-bootstrap';
import { FaFilePdf } from 'react-icons/fa';
import { FlexRow } from 'motion-components';

interface StandaloneCollectionsSectionProps {
  id: number;
}

const SortIcons = {
  asc: (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 448 512"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
      style={{ marginLeft: '0.3rem', marginTop: '-4px' }}
    >
      <path d="M34.9 289.5l-22.2-22.2c-9.4-9.4-9.4-24.6 0-33.9L207 39c9.4-9.4 24.6-9.4 33.9 0l194.3 194.3c9.4 9.4 9.4 24.6 0 33.9L413 289.4c-9.5 9.5-25 9.3-34.3-.4L264 168.6V456c0 13.3-10.7 24-24 24h-32c-13.3 0-24-10.7-24-24V168.6L69.2 289.1c-9.3 9.8-24.8 10-34.3.4z" />
    </svg>
  ),
  desc: (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 448 512"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
      style={{ marginLeft: '0.3rem', marginTop: '-4px' }}
    >
      <path d="M413.1 222.5l22.2 22.2c9.4 9.4 9.4 24.6 0 33.9L241 473c-9.4 9.4-24.6 9.4-33.9 0L12.7 278.6c-9.4-9.4-9.4-24.6 0-33.9l22.2-22.2c9.5-9.5 25-9.3 34.3.4L184 343.4V56c0-13.3 10.7-24 24-24h32c13.3 0 24 10.7 24 24v287.4l114.8-120.5c9.3-9.8 24.8-10 34.3-.4z" />
    </svg>
  ),
};

const columns = [
  { key: 'code', label: 'Κωδικός Συλλογής', sortable: true },
  { key: 'dateCollected', label: 'Ημερομηνία', sortable: true },
  { key: 'collectionLines', label: 'Απόβλητα', sortable: false },
  { key: 'actions', label: 'Ενέργειες', sortable: false },
];

function apiBase() {
  const url = process.env.REACT_APP_WEBAPI.replace('/graphql', ``);
  return url;
}

/** Γραμμές αποβλήτων — ίδια εμφάνιση με τον πίνακα αιτήσεων */
const CollectionLinesCell = ({ lines }: { lines: any[] }) => {
  if (!lines?.length) {
    return <span style={{ color: '#9a9a9a', fontStyle: 'italic' }}>—</span>;
  }

  return (
    <div className="home-collection-lines">
      {lines.map((line, idx) => {
        const w = line.netWeight;
        const weightStr =
          typeof w === 'number'
            ? w.toLocaleString('el-GR', { maximumFractionDigits: 2 })
            : String(w ?? '');
        return (
          <div key={idx} className="home-collection-line">
            <span className="home-collection-line__material">{line.material?.name}</span>
            <span className="home-collection-line__weight">{weightStr} kg</span>
          </div>
        );
      })}
    </div>
  );
};

export const StandaloneCollectionsSection: React.FC<StandaloneCollectionsSectionProps> = ({
  id,
}) => {
  const { handleGraphqlError } = useHandleError('standalonecollections');
  const [getCollections, { data: rawData, loading, error }] = useLazyQuery(gqlGetCollections);
  const [sortColumn, setSortColumn] = useState('dateCollected');
  const [sortDirection, setSortDirection] = useState('desc');
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);

  const collections = rawData?.getCollections?.data ?? [];
  const totalCount = rawData?.getCollections?.recordCount ?? 0;

  const handleCollectionCertificateClick = (collectionId: number) => {
    const url = `${apiBase()}/pdf/waste-reception-certificate/${collectionId}`;
    window.open(url, '_blank').focus();
  };

  const handleConsignmentNoteClick = (collectionId: number) => {
    const url = `${apiBase()}/pdf/consignment-note/${collectionId}`;
    window.open(url, '_blank').focus();
  };

  const handleSort = (column: string) => {
    setSortDirection((prev) => (sortColumn === column && prev === 'desc' ? 'asc' : 'desc'));
    setSortColumn(column);
  };

  const fetchCollections = useCallback(() => {
    getCollections({
      variables: {
        where: JSON.stringify({
          collectionPointId: id,
          isActive: true,
          withoutCollectionOrder: true,
        }),
        offset: (page - 1) * limit,
        limit,
        order: `${sortColumn ?? 'dateCollected'} ${sortDirection}`,
      },
    });
  }, [getCollections, id, sortColumn, sortDirection, limit, page]);

  useEffect(() => {
    if (id) fetchCollections();
  }, [fetchCollections, id]);

  useEffect(() => {
    setPage(1);
  }, [sortColumn, sortDirection, limit]);

  if (error) {
    handleGraphqlError('query-get', error);
    return null;
  }

  // Απόκρυψη ολόκληρου του τμήματος όταν δεν υπάρχουν συλλογές χωρίς αίτηση
  if (rawData && totalCount === 0) return null;
  if (loading && !rawData) return null;

  const formatDate = (isoString: string) => new Date(isoString).toLocaleDateString('el-GR');

  return (
    <Card className="home-section-card shadow-sm mt-4">
      <Card.Header>
        <FlexRow>
          <Card.Title className="m-0 home-section-title">Συλλογές χωρίς Αίτηση</Card.Title>
        </FlexRow>
      </Card.Header>
      <Card.Body>
        <div className="home-table-wrap">
            <table className="home-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={[
                        col.sortable ? 'sortable' : '',
                        col.sortable && sortColumn === col.key ? 'sorted' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      {col.label}
                      {col.sortable && sortColumn === col.key && SortIcons[sortDirection]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {collections.map((collection: any) => (
                  <tr key={collection.id}>
                    <td style={{ textAlign: 'center' }}>{collection.code}</td>
                    <td style={{ textAlign: 'center' }}>
                      {collection.dateCollected ? formatDate(collection.dateCollected) : '—'}
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      <CollectionLinesCell lines={collection.collectionLines} />
                    </td>
                    <td>
                      <div className="home-row-actions">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleCollectionCertificateClick(collection.id)}
                        >
                          <FaFilePdf className="mr-1" /> Βεβαίωση Παραλαβής
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleConsignmentNoteClick(collection.id)}
                        >
                          <FaFilePdf className="mr-1" /> Δελτίο Αποστολής
                        </Button>
                        {collection.idocs?.length > 0 && (
                          <Button
                            size="sm"
                            variant="outline-warning"
                            onClick={() => {
                              const idocId = collection.idocs[0].id;
                              const url = `${apiBase()}/idoc/${idocId}/gr`;
                              window.open(url, '_blank').focus();
                            }}
                          >
                            Έντυπο Αναγνώρισης
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        {totalCount > 0 && (
          <>
            <div className="home-section-footer">
              <small>(*) Παρουσιάζονται οι τελευταίες (έως {limit}) συλλογές ανά σελίδα.</small>
              <div>
                <label style={{ marginBlock: 'auto' }}>
                  Πλήθος συλλογών: <strong>{totalCount}</strong>
                </label>
                &nbsp;
                <select
                  className="home-page-size-select"
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                >
                  <option value={5}>5</option>
                  <option value={7}>7</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {collections.length > 0 && totalCount > limit && (
              <div className="home-pagination">
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="mr-2"
                >
                  Προηγούμενη
                </Button>
                <span style={{ fontSize: '0.9rem', color: '#666' }}>
                  Σελίδα {page} από {Math.ceil(totalCount / limit)}
                </span>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() =>
                    setPage((p) => (p < Math.ceil(totalCount / limit) ? p + 1 : p))
                  }
                  disabled={page >= Math.ceil(totalCount / limit)}
                  className="ml-2"
                >
                  Επόμενη
                </Button>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};
